import { Grid, Typography, useEventCallback } from '@mui/material';
import { PecForm } from '.';
import PmiTextField from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { memo } from 'react';

interface FormPecEditProps {
  values?: PecForm;
  errors?: FormikErrors<PecForm>;
  touched?: FormikTouched<PecForm>;
  onChange: (key: keyof PecForm, value: PecForm[keyof PecForm]) => void;
}

const FormPecEditComponent = ({
  values,
  errors,
  touched,
  onChange,
}: FormPecEditProps): React.ReactElement => {
  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      const nameSplit = file.name ? file.name.split('.') : [];

      const document: TypeFile = {
        ...file,
        raw: (result as string)?.split(',')[1] as string,
        name: file.name || '',
        tipoAllegato: file.type || '',
        type: nameSplit.length ? nameSplit[nameSplit.length - 1]?.toUpperCase() : '',
      };

      onChange('attachments', [document]);
    });
    reader.readAsDataURL(file);
  });

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12}>
        <Typography variant="body1">
          <strong>Da:</strong> {values?.sender}
        </Typography>
      </Grid>
      <Grid item xs={12} md={7}>
        <PmiTextField
          label="A *"
          aria-label="A"
          id="receiver"
          name="receiver"
          fullWidth
          helperText={errors?.receiver}
          error={touched?.receiver && Boolean(errors?.receiver)}
          type="text"
          variant="outlined"
          value={values?.receiver || ''}
          onChange={(event) => {
            onChange('receiver', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          label="Destinatario"
          aria-label="Destinatario"
          id="descRecipient"
          name="descRecipient"
          fullWidth
          helperText={errors?.descRecipient}
          error={touched?.descRecipient && Boolean(errors?.descRecipient)}
          type="text"
          variant="outlined"
          value={values?.descRecipient || ''}
          onChange={(event) => {
            onChange('descRecipient', event.target.value);
          }}
        />
      </Grid>
      {values?.cc ? (
        <Grid item xs={12}>
          <Typography variant="body1">
            <strong>CC:</strong> {values?.cc}
          </Typography>
        </Grid>
      ) : undefined}
      <Grid item xs={12}>
        <PmiTextField
          label="Oggetto *"
          aria-label="Oggetto"
          id="subject"
          name="subject"
          fullWidth
          helperText={errors?.subject}
          error={touched?.subject && Boolean(errors?.subject)}
          type="text"
          variant="outlined"
          value={values?.subject || ''}
          onChange={(event) => {
            onChange('subject', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        Carica allegato:&nbsp;
        <PmiInputFile
          file={values?.attachments?.length ? (values?.attachments[0] as File) : undefined} // gestione per singolo allegato
          onChange={(file) => handleAddFile(file)}
          color="cdmBrandThird"
          showFilename={false}
          multiple={false}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1">
          <strong>Allegati:</strong>
          {values?.attachments?.map((currentFile) => {
            return (
              <span key={`key-${currentFile?.name}`}>
                <a
                  download={currentFile?.name}
                  href={currentFile.raw ? `data:application/pdf;base64, ${currentFile.raw}` : ''}
                >
                  {currentFile?.name}
                </a>
              </span>
            );
          })}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Corpo *"
          aria-label="Corpo"
          id="messageBody"
          name="messageBody"
          fullWidth
          helperText={errors?.messageBody}
          error={touched?.messageBody && Boolean(errors?.messageBody)}
          type="text"
          multiline
          rows={5}
          variant="outlined"
          value={values?.messageBody || ''}
          onChange={(event) => {
            onChange('messageBody', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormPecEditComponent.displayName = 'Form Pec Edit component';

const FormPecEdit = memo(FormPecEditComponent);

export default FormPecEdit;
